import { BuildClass, Do, fsmData, timer } from '../../universal';
import { React, _ } from '../lib';
import { J2rButton } from './component-buttons';
import { J2rComboBox } from './component-combobox';
import { J2rDatetime } from './component-date-picker';
import { validateTime } from './component-main';
import { J2rHelp, J2rLoadingSpinner, J2rModalPortal, J2rText, j2r, } from './component-react';
import { J2rListComponent } from './component-react-list';
import { moment } from './moment-wrapper';
import { Bindings, ConditionalObject, stubDOMRect } from './ui5';
export class J2rEditableGrid extends React.Component {
    static defaultProps = {
        cl: '',
        pk: x => {
            if (x == null) {
                throw new Error('Grid pk function received null/undefined record');
            }
            return x['ID'];
        },
        onUpdate: _.noop,
        onBeforeUpdate: null,
        onBeforeDelete: null,
        onClick: null,
        onKeyPress: null,
        appendable: false,
        deletable: false,
        sortable: true,
        selectable: false,
        editable: true,
    };
    static Components = {
        Text: (obj = {}) => ({
            component: () => ({
                tag: J2rText,
                readOnly: obj.readOnly,
                maxLength: obj.maxLength,
                placeholder: obj.placeholder,
                fixer: obj.fixer,
                onFocus: obj.onFocus ?? _.noop,
                onChange: obj.onChange ?? _.noop,
            }),
            renderer: (v, r) => {
                if (obj.renderer) {
                    const val = obj.renderer(v, r);
                    if (typeof val == 'object') {
                        return val;
                    }
                    return String(val);
                }
                return String(v ?? '');
            },
            validator: (v) => {
                const length = v?.length || 0;
                const [min, max] = [obj.minLength ?? 0, obj.maxLength ?? 1e99];
                if (!(min <= length && length <= max)) {
                    return false;
                }
                if (obj.numeric && v != null && isNaN(+v)) {
                    return false;
                }
                return true;
            },
            fixer: (v) => {
                let str = String(v ?? '');
                if (obj.trim) {
                    str = str.trim();
                }
                str = (obj.fixer ?? _.identity)(str);
                if (isNaN(str)) {
                    return str || null;
                }
                return str ?? null;
            },
            sortVal: obj.sortVal ?? ((v) => String(v ?? '').toLowerCase()),
        }),
        TextTime: (obj = {}) => ({
            component: () => ({
                tag: J2rText,
                maxLength: 5,
                placeholder: obj.placeholder ?? 'HH:MM',
                readOnly: obj.readOnly ? obj.readOnly : undefined,
                onFocus: obj.onFocus ?? _.noop,
            }),
            renderer: (v, r) => {
                if (obj.renderer && typeof obj.renderer === 'function') {
                    return String(obj.renderer(v, r));
                }
                return String(v ?? '');
            },
            validator: (v) => {
                const str = String(v ?? '');
                const length = str.length;
                const min = typeof obj.minLength === 'number' ? obj.minLength : 0;
                const max = typeof obj.maxLength === 'number' ? obj.maxLength : 1e99;
                return min <= length && length <= max;
            },
            fixer: (v) => {
                if (obj.fixer && typeof obj.fixer === 'function') {
                    return obj.fixer(v);
                }
                return validateTime(String(v));
            },
            sortVal: (v) => v ?? '',
        }),
        Checkbox: (obj = {}) => ({
            component: () => ({
                tag: J2rGridCheckbox,
                isCross: !(obj.tick ?? true),
            }),
            renderer: (v) => ({
                cl: 'cntr',
                text: Do(() => {
                    if (!v) {
                        return '';
                    }
                    else if (obj.tick ?? true) {
                        return '✔';
                    }
                    return '✘';
                }),
            }),
            fixer: (v) => Boolean(v),
            clickThrough: true,
        }),
        Currency: () => ({
            renderer: (v) => {
                if (typeof v === 'string') {
                    return v;
                }
                const num = typeof v === 'number' ? v : Number(v);
                return isNaN(num) ? '' : num.toFixed(2);
            },
            fixer: (v) => {
                const vn = typeof v === 'number' ? v : typeof v === 'string' ? +v : Number(v);
                if (!v) {
                    return null;
                }
                else if (isNaN(vn)) {
                    return String(v);
                }
                return vn;
            },
            validator: (v) => typeof v !== 'string',
        }),
        Dropdown: (options, obj = {}, nullable = false) => {
            const opts = _.flattenDeep([
                fsmData(options, {
                    filter: (x) => typeof x === 'object' &&
                        x != null &&
                        'options' in x &&
                        x.options != null,
                    map: (x) => x.options ?? [],
                }),
                fsmData(options, {
                    filter: (x) => typeof x === 'object' &&
                        x != null &&
                        'value' in x &&
                        x.value != null,
                    map: (x) => x ?? [],
                }),
            ]);
            if (nullable) {
                opts.unshift({ value: '', text: '' });
            }
            const optMap = _.fromPairs(_.map(opts, x => [x.value, x.text]));
            return {
                component: () => ({
                    tag: J2rComboBox,
                    options,
                    onFocus: obj.onFocus ?? _.noop,
                    onChange: obj.onChange ?? _.noop,
                    nullable: nullable ? '' : null,
                }),
                fixer: (v) => v || '',
                def: () => '',
                validator: (v) => {
                    if (v == null && nullable) {
                        v = '';
                    }
                    if (obj.validator) {
                        return obj.validator(v, opts);
                    }
                    return _.size(opts.filter(x => x.value === v)) > 0;
                },
                renderer: (v) => optMap[String(v)] ?? '',
                sortVal: (v) => optMap[String(v)] ?? '',
            };
        },
        DropdownNullable: (options, obj = {}) => J2rEditableGrid.Components.Dropdown(options, obj, true),
        Date: (obj = {}) => ({
            component: () => ({ tag: J2rDatetime }),
            renderer: (v) => {
                if (!v) {
                    return '';
                }
                const dateStr = String(v);
                if (obj.format && typeof obj.format === 'string') {
                    return moment(dateStr).format(obj.format);
                }
                else if (obj.long) {
                    return moment(dateStr).format('DD/MM/YYYY');
                }
                return moment(dateStr).format('DD/MM/YY');
            },
        }),
        Color: (obj = {}) => ({
            component: () => ({
                tag: J2rGridColor,
                readonly: obj.readonly,
            }),
            renderer: (v) => ({
                cl: 'j2r-color-picker',
                style: { backgroundColor: `#${v}` },
            }),
            validator: (v) => v == null || String(v).length === 6,
            clickThrough: true,
        }),
    };
    mounted;
    constructor(props) {
        super(props);
        this.mounted = false;
        Bindings(this, [
            this.buildButtons,
            this.buildCell,
            this.buildList,
            this.changeSelectedValue,
            this.checkAppendable,
            this.clearSelection,
            this.commitCurrentWork,
            this.deleteRecord,
            this.getDelta,
            this.getResetData,
            this.getRowsOrdered,
            this.handleKeyPress,
            this.isChanged,
            this.isRecordEmpty,
            this.moveSelection,
            this.onSave,
            this.onUpdate,
            this.resetForm,
            this.selectCell,
            this.updateCellValue,
            this.updateSortOrder,
        ]);
        this.state = this.initState(props);
    }
    componentDidMount() {
        this.mounted = true;
    }
    componentWillUnmount() {
        this.mounted = false;
    }
    render() {
        return j2r({
            cl: BuildClass({
                j2rgrid: true,
                ...(this.props.cl ? { [this.props.cl]: true } : {}),
            }),
            children: [this.buildList(), this.buildButtons(), this.buildResponse()],
        });
    }
    initState(props) {
        let state = {
            isLoading: false,
            isSuccess: false,
            message: '',
            error: '',
            selectedRow: null,
            selectedCol: null,
            selectedValue: null,
            sortingKey: '',
            sortingFunction: _.identity,
            sortingReverse: false,
            data: [],
        };
        if (props.sortable && props.defaultSortingKey) {
            state = _.assign(state, this.updateSortOrder(state, props.defaultSortingKey));
            if (props.defaultSortingReverse != null) {
                state.sortingReverse = props.defaultSortingReverse;
            }
        }
        state.data = this.getResetData(props);
        return state;
    }
    getResetData(props) {
        const data = fsmData(props.data, {
            map: (record, index) => {
                const defaults = {
                    __id: index,
                    __deleted: false,
                    __appended: false,
                };
                return _.assign({}, defaults, record);
            },
        });
        _.forEach(data, (record, index) => _.forEach(props.fields, field => {
            if (!_.has(record, field.key)) {
                console.error(`Record #${index} is missing field ${field.key}`);
            }
        }));
        return this.checkAppendable({ data: data }, props);
    }
    componentDidUpdate(prevProps) {
        const to_json = (x) => JSON.stringify(x, (_k, v) => {
            if (typeof v === 'function') {
                return v.toString();
            }
            return v;
        });
        const o = to_json(prevProps);
        const n = to_json(this.props);
        if (!_.isEqual(o, n)) {
            this.setState(this.initState(this.props));
        }
    }
    checkAppendable(state, props) {
        if (!props.appendable) {
            return state.data ?? [];
        }
        const currentData = state.data ?? [];
        const is_empty = this.isRecordEmpty(fsmData(currentData, {
            filter: x => !x.__deleted,
            sort: x => x.__id,
            reverse: true,
            takeFirst: true,
        }));
        if (is_empty) {
            return currentData;
        }
        const new_record_partial = {};
        _.forEach(props.fields, field => {
            new_record_partial[field.key] = (field.def ?? field?.cmpt?.def ?? _.noop)();
        });
        const new_record = {
            ...new_record_partial,
            __id: (currentData[currentData.length - 1]?.__id ?? -1) + 1,
            __deleted: false,
            __appended: true,
        };
        const data = currentData.concat(new_record);
        return data;
    }
    isRecordEmpty(record, props = this.props) {
        if (record == null) {
            return false;
        }
        let is_empty = true;
        _.forEach(props.fields, field => {
            const def_val = (field.def ?? field.cmpt?.def ?? _.noop)();
            if (record[field.key] !== def_val) {
                is_empty = false;
                return false;
            }
            return true;
        });
        return is_empty;
    }
    isRecordValid(record) {
        if (this.isRecordEmpty(record)) {
            return true;
        }
        let invalid = false;
        _.forEach(this.props.fields, field => {
            const is_valid = (field.cmpt?.validator ?? _.stubTrue)(record[field.key]);
            if (!is_valid) {
                invalid = true;
                return false;
            }
            return true;
        });
        return !invalid;
    }
    isRecordChanged(record) {
        if (this.isRecordEmpty(record)) {
            return false;
        }
        const keys = this.props.fields.map(f => f.key);
        let old_record = this.props.data[record.__id];
        old_record = _.pick(old_record, keys);
        const new_record = _.pick(record, keys);
        const result = !_.isEqual(old_record, new_record);
        return result;
    }
    isRecordDisabled(record) {
        const disabled = record['disabled'];
        return typeof disabled === 'boolean' ? disabled : Boolean(disabled);
    }
    buildList() {
        return {
            tag: J2rListComponent,
            key: 'list',
            value: null,
            disableFocusControl: true,
            lazyRenderHeight: this.props.lazyRenderHeight,
            headingRow: {
                cl: 'gridRow noselect heading',
                children: _.flatten([
                    ConditionalObject(this.props.editable ?? false, {
                        key: 'edited-indicator',
                        cl: 'edited-indicator',
                    }),
                    this.props.fields.map(field => ({
                        tag: 'span',
                        cl: BuildClass({
                            ...(field.cl ? { [field.cl]: true } : {}),
                            [field.key.toLowerCase().replace(/([^a-z0-9])/g, '')]: true,
                            'heading-cell': true,
                            'help-indicator': field.tooltipBubble ?? false,
                        }),
                        key: field.key,
                        title: !field.tooltipBubble ? field.lblDesc : undefined,
                        children: [
                            {
                                key: 'heading-cell-inner',
                                title: field.tooltipBubble ? field.lblDesc : undefined,
                                cl: BuildClass({
                                    'txt heading-cell-inner': true,
                                    sorted: field.key === this.state.sortingKey &&
                                        this.props.sortable,
                                    'sort-asc': field.key === this.state.sortingKey &&
                                        this.props.sortable &&
                                        !this.state.sortingReverse,
                                    'sort-dsc': field.key === this.state.sortingKey &&
                                        this.props.sortable &&
                                        this.state.sortingReverse,
                                }),
                                text: field.lbl,
                            },
                            ConditionalObject(field.tooltipBubble ?? false, {
                                tag: J2rHelp,
                                key: 'help-tooltip',
                                title: field.lblDesc,
                            }),
                        ],
                        onClick: () => {
                            this.setState(s => {
                                if (field.onClick != null) {
                                    return field?.onClick(this.state) ?? {};
                                }
                                else if (this.props.sortable) {
                                    return this.updateSortOrder(s, field.key);
                                }
                                return {};
                            });
                        },
                    })),
                    ConditionalObject(this.props.deletable ?? false, {
                        key: 'delete-record',
                        cl: 'delete-record heading',
                    }),
                ]),
            },
            items: this.getRowsOrdered().map((record, indexY) => ({
                value: this.state.data.indexOf(record),
                cl: BuildClass({
                    gridRow: true,
                    empty: this.isRecordEmpty(record),
                    changed: this.isRecordChanged(record),
                    disabled: this.isRecordDisabled(record),
                }),
                children: _.flatten([
                    ConditionalObject(this.props.editable ?? false, {
                        key: 'edited-indicator',
                        cl: 'edited-indicator',
                    }),
                    this.props.fields.map((field, indexX) => this.buildCell(record, field, indexY, indexX)),
                    ConditionalObject(this.props.deletable ?? false, {
                        tag: 'span',
                        key: 'delete-record',
                        cl: 'delete-record',
                        children: [
                            {
                                tag: 'img',
                                key: 'delete-inner',
                                src: '/static/img/cross.png',
                            },
                        ],
                        onClick: () => {
                            let canDelete = true;
                            if (this.props.onBeforeDelete &&
                                !this.props.onBeforeDelete(record)) {
                                canDelete = false;
                            }
                            if (canDelete) {
                                this.deleteRecord(record.__id);
                            }
                        },
                    }),
                ]),
            })),
        };
    }
    buildButtons() {
        if (this.props.onSave != null) {
            return {
                key: 'buttons',
                cl: 'cntr buttons',
                children: _.flatten([
                    {
                        tag: J2rButton,
                        type: 'submit',
                        key: 'save',
                        label: 'Save',
                        title: 'Save',
                        onClick: this.onSave,
                    },
                    ConditionalObject(this.props.editable ?? false, {
                        tag: J2rButton,
                        type: 'standard',
                        key: 'reset',
                        label: 'Reset',
                        title: 'Reset',
                        onClick: this.resetForm,
                    }),
                    ConditionalObject(_.size(_.filter(this.state.data, R => R?.__deleted)) > 0, {
                        tag: J2rButton,
                        type: 'standard',
                        key: 'restore-deleted',
                        label: 'Restore',
                        title: 'Restores deleted records without resetting other changes',
                        onClick: () => {
                            this.setState({
                                message: 'Restored deleted records',
                                data: this.state.data.map(R => {
                                    R = _.clone(R);
                                    R.__deleted = false;
                                    return R;
                                }),
                            }, () => timer(2000, () => {
                                if (this.mounted) {
                                    this.setState({ message: '' });
                                }
                            }));
                        },
                    }),
                    ConditionalObject(this.props.buttons != null, () => _.map(this.props.buttons, button => ({
                        tag: J2rButton,
                        type: 'standard',
                        key: button.label,
                        label: button.label,
                        title: button.title ?? button.label ?? '',
                        onClick: () => {
                            this.selectCell(null, null, () => {
                                this.setState(s => (button.onClick != null
                                    ? button.onClick(s)
                                    : {}) ?? {});
                            });
                        },
                    }))),
                ]),
            };
        }
        return null;
    }
    buildResponse() {
        if (!this.props.editable) {
            return null;
        }
        const response = Do(() => {
            if (this.state.isLoading) {
                return {
                    tag: J2rLoadingSpinner,
                    key: 'loading',
                    size: 'small',
                };
            }
            if (this.state.error) {
                return String(this.state.error);
            }
            if (this.state.message) {
                return {
                    key: 'non-error',
                    cl: 'non-error',
                    text: this.state.message,
                };
            }
            return {
                cl: 'stub',
                key: 'nothing',
                text: '-',
            };
        });
        return {
            key: 'response',
            cl: 'response-label',
            children: [response],
        };
    }
    deleteRecord(index) {
        const fn = (s) => {
            const data = _.clone(s.data);
            data[index] = _.assign({}, s.data[index], { __deleted: true });
            return { data };
        };
        this.setState(fn, () => {
            const delta = (s) => ({
                data: this.checkAppendable(s, this.props),
            });
            this.setState(delta, this.onUpdate);
        });
    }
    getRowsOrdered() {
        let records = fsmData(this.state.data, {
            filter: R => R != null && !R.__deleted,
            reverse: this.state.sortingReverse,
            sort: R => {
                if (this.props.sortable) {
                    return this.state.sortingFunction(R[this.state.sortingKey]);
                }
                return 1;
            },
        });
        records = _.sortBy(records, R => R.__appended);
        return _.sortBy(records, R => this.isRecordEmpty(R));
    }
    buildCell(record, field, indexY, indexX) {
        const isSelected = this.state.selectedRow === record.__id && this.state.selectedCol === field.key;
        const isChanged = Do(() => {
            if (record.__appended) {
                return false;
            }
            return !_.isEqual(record[field.key], this.props.data[record.__id]?.[field.key]);
        });
        const isValid = (field.cmpt?.validator ?? _.stubTrue)(record[field.key]);
        const selectedValue = isSelected ? this.state.selectedValue : null;
        return {
            tag: J2rEditableGridCell,
            key: field.key,
            field,
            record,
            tabIndex: 1 + indexY * this.props.fields.length + indexX,
            isEditable: this.props.editable,
            isSelected,
            isChanged,
            isValid,
            selectedValue,
            cl: field.cl,
            selectCell: this.selectCell,
            commitCurrentWork: this.commitCurrentWork,
            changeValue: this.changeSelectedValue,
            handleKeyPress: this.handleKeyPress,
        };
    }
    changeSelectedValue(v, cb) {
        this.setState({ selectedValue: v }, () => {
            ;
            (cb ?? _.noop)(v);
        });
    }
    updateCellValue(row_index, field_key, value, cb) {
        if (row_index == null || field_key == null) {
            ;
            (cb ?? _.noop)();
            return;
        }
        const field = fsmData(this.props.fields, {
            filter: x => x.key === field_key,
            takeFirst: true,
        });
        const fixer_function = field.cmpt?.fixer ?? _.identity;
        if (field.readonly) {
            ;
            (cb ?? _.noop)();
            return;
        }
        const fixedValue = fixer_function(value);
        const rowIndex = row_index;
        const fieldKey = field_key;
        const update = (s) => {
            let record = _.assign({}, s.data?.[rowIndex], { [fieldKey]: fixedValue });
            const delta = (this.props.recordValidation ?? _.noop)(record, fieldKey);
            if (delta != null) {
                record = _.assign({}, record, delta);
            }
            return { data: _.assign([], s.data, { [rowIndex]: record }) };
        };
        let canUpdate = true;
        if (this.props.onBeforeUpdate != null &&
            !this.props.onBeforeUpdate({
                rowID: row_index,
                field: field_key,
                value: fixedValue,
                data: this.getData(),
            })) {
            canUpdate = false;
        }
        if (canUpdate) {
            this.setState(update, () => {
                this.setState(s => ({ data: this.checkAppendable(s, this.props) }), () => {
                    if (this.state.selectedRow != null &&
                        this.state.selectedCol != null &&
                        this.state.data?.[this.state.selectedRow]?.[this.state.selectedCol] !== fixer_function(this.state.selectedValue)) {
                        this.updateCellValue(this.state.selectedRow, this.state.selectedCol, this.state.selectedValue, cb);
                    }
                    else {
                        ;
                        (cb ?? _.noop)();
                        this.onUpdate();
                    }
                });
            });
        }
    }
    onUpdate() {
        if (this.props.onUpdate != null) {
            this.props.onUpdate({
                data: this.getData(),
                delta: this.getDelta(),
            });
        }
    }
    handleKeyPress(e) {
        if (this.props.onKeyPress) {
            this.props.onKeyPress(e);
            return;
        }
        switch (e.keyCode) {
            case 38:
                this.moveSelection(-1, 0);
                break;
            case 40:
                this.moveSelection(1, 0);
                break;
            case 9:
                if (e.shiftKey) {
                    this.moveSelection(0, -1);
                }
                else {
                    this.moveSelection(0, 1);
                }
                break;
            case 13:
                this.moveSelection(1, 0);
                break;
            case 27:
                this.selectCell(null, null);
                break;
            case 45:
                this.selectCell(this.state.data.length - 1, this.state.selectedCol);
                break;
            default:
                return;
        }
        e.stopPropagation();
        e.preventDefault();
    }
    updateSortOrder(current_state, key) {
        const cs = current_state;
        const field = fsmData(this.props.fields, {
            filter: F => F.key === key,
            takeFirst: true,
        });
        return {
            sortingKey: key,
            sortingReverse: cs?.sortingKey === key && !cs?.sortingReverse,
            sortingFunction: field.cmpt?.sortVal ?? _.identity,
        };
    }
    selectCell(row_index, column_field, cb) {
        this.updateCellValue(this.state.selectedRow, this.state.selectedCol, this.state.selectedValue, () => {
            const update = {
                selectedRow: row_index,
                selectedCol: column_field,
                selectedValue: this.state.data?.[row_index ?? -1]?.[column_field ?? ''] ?? null,
            };
            this.setState(update, () => {
                this.props.onClick?.(update);
                (cb ?? _.noop)();
            });
        });
    }
    moveSelection(dy, dx) {
        const fields = this.props.fields.map(F => F.key);
        let cx = this.state.selectedCol != null ? fields.indexOf(this.state.selectedCol) : -1;
        let cy = this.state.selectedRow;
        const mapping = {};
        const mapping_reverse = {};
        this.getRowsOrdered().forEach((record, index) => {
            mapping[record.__id] = index;
            mapping_reverse[index] = record.__id;
        });
        cy = mapping[cy ?? -1] ?? 0;
        cx += dx;
        cy += dy;
        while (cx < 0 && cy > 0) {
            cx += this.props.fields.length;
            cy -= 1;
        }
        if (cx < 0) {
            cx = 0;
        }
        while (cx >= this.props.fields.length && cy < this.state.data.length - 1) {
            cx -= this.props.fields.length;
            cy += 1;
        }
        if (cx >= this.props.fields.length) {
            cx = this.props.fields.length - 1;
        }
        if (cy < 0) {
            cy = 0;
        }
        if (cy >= this.state.data.length) {
            cy = this.state.data.length - 1;
        }
        cy = mapping_reverse[cy];
        this.selectCell(cy, this.props.fields[cx]?.key);
    }
    clearSelection(cb) {
        this.selectCell(null, null, cb);
    }
    getData() {
        return this.state.data;
    }
    getDelta() {
        const deleted_records = [];
        const appended_records = [];
        const updated_records = {};
        const record_count = _.size(this.state.data);
        _.range(record_count).forEach(index => {
            const record_old = this.props.data[index];
            const record_new = this.state.data?.[index];
            if (!record_new) {
                return;
            }
            const pk = this.props.pk(record_new);
            if (record_new.__deleted) {
                if (!record_new.__appended) {
                    deleted_records.push(pk);
                }
                return;
            }
            if (record_old == null) {
                if (!this.isRecordEmpty(record_new)) {
                    const keys = _.map(this.props.fields, f => f.key);
                    appended_records.push(_.pick(record_new, keys));
                }
                return;
            }
            const diff_record = {};
            this.props.fields.forEach(field => {
                const vold = record_old[field.key];
                const vnew = record_new[field.key];
                if (vold !== vnew) {
                    diff_record[field.key] = vnew;
                }
            });
            if (_.size(diff_record) > 0) {
                updated_records[pk] = diff_record;
            }
        });
        const diff_count = _.sum([
            appended_records.length,
            _.size(updated_records),
            deleted_records.length,
        ]);
        return {
            appended: appended_records,
            updated: updated_records,
            deleted: deleted_records,
            isChanged: diff_count > 0,
        };
    }
    isChanged() {
        return this.getDelta().isChanged;
    }
    resetForm() {
        this.clearSelection(() => {
            this.setState({
                isLoading: false,
                isSuccess: false,
                message: 'Grid Reset',
                error: '',
                data: this.getResetData(this.props),
            }, () => {
                this.setState(s => {
                    let selectedRow = null;
                    let selectedCol = null;
                    let selectedValue = null;
                    if (s.selectedRow != null && s.data?.[s.selectedRow] != null) {
                        selectedRow = s.data[s.selectedRow]?.__id ?? null;
                        selectedCol = s.selectedCol ?? null;
                        if (s.selectedCol != null) {
                            selectedValue =
                                s.data[s.selectedRow]?.[s.selectedCol] ?? null;
                        }
                    }
                    return {
                        selectedRow,
                        selectedCol,
                        selectedValue,
                    };
                }, this.onUpdate);
                timer(2000, () => {
                    if (this.mounted) {
                        this.setState({ message: '' });
                    }
                });
            });
        });
    }
    commitCurrentWork(cb) {
        if (!this.mounted) {
            return;
        }
        this.updateCellValue(this.state.selectedRow, this.state.selectedCol, this.state.selectedValue, cb);
    }
    onSave() {
        this.setState({
            isLoading: true,
            isSuccess: false,
        });
        this.commitCurrentWork(() => {
            const delta = this.getDelta();
            if (!delta.isChanged) {
                this.setState({
                    isLoading: false,
                    isSuccess: false,
                    message: 'Nothing has changed',
                    error: '',
                }, () => timer(2000, () => {
                    if (this.mounted) {
                        this.setState({ message: '' });
                    }
                }));
            }
            else {
                this.props.onSave?.(delta, cb_response => {
                    if (cb_response === true) {
                        timer(() => {
                            if (this.mounted) {
                                this.clearSelection(() => {
                                    this.setState({
                                        isLoading: false,
                                        isSuccess: true,
                                        message: 'Successfully Saved!',
                                        error: '',
                                        data: this.getResetData(this.props),
                                    }, () => timer(2000, () => {
                                        if (this.mounted) {
                                            this.setState({
                                                message: '',
                                                isSuccess: false,
                                            });
                                        }
                                    }));
                                });
                            }
                        });
                    }
                    else {
                        this.setState({
                            isLoading: false,
                            isSuccess: false,
                            message: '',
                            error: cb_response,
                        });
                    }
                });
            }
        });
    }
}
export class J2rEditableGridCell extends React.Component {
    selectedComponent;
    mounted;
    constructor(props) {
        super(props);
        this.selectedComponent = React.createRef();
        this.mounted = false;
    }
    shouldComponentUpdate(nextProps) {
        const prevProps = this.props;
        const ko = _.keys(prevProps);
        const kn = _.keys(nextProps);
        const keys = _.uniq(_.concat(ko, kn));
        const diffs = _.filter(keys, k => prevProps[k] !== nextProps[k] && !['field', 'record'].includes(k));
        if (diffs.length > 0) {
            return true;
        }
        if (!_.isEqual(prevProps.field, nextProps.field)) {
            return true;
        }
        if (!_.isEqual(prevProps.record, nextProps.record)) {
            return true;
        }
        return false;
    }
    componentDidMount() {
        this.mounted = true;
    }
    componentWillUnmount() {
        this.mounted = false;
    }
    render() {
        return j2r(Do(() => {
            if (!this.props.isSelected || !this.props.isEditable) {
                return this.buildDisplay();
            }
            const is_ro = this.props.field.readOnlyFunc?.(this.props.record) ??
                this.props.field.readonly;
            if (is_ro) {
                return this.buildDisplay();
            }
            return this.buildEditable();
        }));
    }
    buildDisplay() {
        return {
            tag: 'span',
            key: this.props.field.key,
            tabIndex: this.props.tabIndex,
            cl: BuildClass({
                ...(this.props.cl ? { [this.props.cl]: true } : {}),
                [this.props.field.key.toLowerCase().replace(/([^a-z0-9])/g, '')]: true,
                gridCell: true,
                focused: this.props.isSelected ?? false,
                changed: this.props.isChanged ?? false,
                invalid: !this.props.isValid,
            }),
            children: [
                Do(() => {
                    const renderer = this.props.field.cmpt?.renderer ?? _.identity;
                    const value = this.props.record[this.props.field.key];
                    const rendered = renderer(value, this.props.record);
                    if (rendered == null ||
                        _.includes(['string', 'number'], typeof rendered)) {
                        return {
                            key: 'inner',
                            cl: 'txt',
                            text: String(rendered),
                        };
                    }
                    return _.assign({}, rendered, {
                        key: 'inner',
                        cl: `txt ${rendered.cl ?? ''}`,
                    });
                }),
            ],
            onKeyDown: this.props.handleKeyPress,
            onClick: () => this.props.selectCell?.(this.props.record.__id, this.props.field.key, () => {
                this.clickPassThrough();
            }),
        };
    }
    buildEditable() {
        return {
            tag: 'span',
            key: this.props.field.key,
            tabIndex: this.props.tabIndex,
            cl: BuildClass({
                ...(this.props.cl ? { [this.props.cl]: true } : {}),
                [this.props.field.key.toLowerCase().replace(/([^a-z0-9])/g, '')]: true,
                gridCell: true,
                editing: true,
                focused: this.props.isSelected ?? false,
            }),
            children: [
                Do(() => {
                    let cmpt = this.props.field.cmpt?.component?.(this) ?? {
                        tag: J2rText,
                    };
                    cmpt = {
                        ...cmpt,
                        key: 'editable-cmpt',
                        ref: this.selectedComponent,
                        autoFocus: true,
                        value: this.props.selectedValue,
                        onUpdate: (v) => {
                            const nextValue = cmpt.tag === J2rComboBox ? v.value : v;
                            const cb = cmpt.onChange ?? _.noop;
                            this.props.changeValue?.(nextValue, cb);
                        },
                    };
                    return cmpt;
                }),
            ],
            onKeyDown: this.props.handleKeyPress,
        };
    }
    clickPassThrough() {
        if (!this.props.field.cmpt?.clickThrough) {
            return;
        }
        timer(() => {
            if (!this.mounted) {
                return;
            }
            const el = this.selectedComponent.current.element?.current;
            if (el == null) {
                console.warn('Cannot proxy-call `clickThrough` on a component without an `element` reference');
            }
            else {
                el.dispatchEvent(new MouseEvent('click', {
                    view: window,
                    bubbles: true,
                    cancelable: true,
                }));
            }
            this.props.commitCurrentWork?.();
        });
    }
}
export class J2rGridColor extends React.Component {
    static palette = [
        ['#4444FF', '#FF6666', '#F61C1C', '#CCCC00'],
        ['#00CC33', '#EC407A', '#AB47BC', '#7E57C2'],
        ['#5C6BC0', '#FF7043', '#29B6F6', '#26A69A'],
        ['#66BB6A', '#D4E157', '#FFA726', '#8D6E63'],
    ];
    element;
    constructor(props) {
        super(props);
        this.element = React.createRef();
        this.state = {
            focused: false,
            closed: false,
        };
    }
    render() {
        return j2r({
            cl: BuildClass({
                'j2r-color-picker': true,
                focused: this.state.focused && !this.state.closed,
            }),
            children: [
                this.buildInput(),
                {
                    tag: J2rModalPortal,
                    key: 'picker',
                    children: [this.buildPicker()],
                },
            ],
        });
    }
    buildInput() {
        const input = {
            tag: 'input',
            key: 'input',
            ref: this.element,
            cl: 'color-picker',
            readOnly: true,
            autoFocus: this.props.autoFocus,
            value: this.props.value ?? '',
            style: { backgroundColor: this.getColorValue() },
        };
        if (!this.props.readonly) {
            return _.assign(input, {
                onFocus: () => {
                    this.setState({
                        focused: true,
                        closed: false,
                    });
                },
                onClick: () => {
                    this.setState({
                        closed: false,
                    });
                },
                onBlur: () => {
                    this.setState({
                        focused: false,
                    });
                },
            });
        }
        return input;
    }
    buildPicker() {
        return {
            cl: BuildClass({
                'color-picker-modal': true,
                closed: this.state.closed || !this.state.focused,
            }),
            key: 'color-picker-modal',
            style: Do(() => {
                const B = this.element.current?.getBoundingClientRect() ?? stubDOMRect;
                const x = (B.left + B.right) / 2;
                const y = B.bottom;
                return {
                    left: `${x - 134}px`,
                    top: `${y + 1}px`,
                };
            }),
            children: Do(() => {
                if (this.state.focused && !this.state.closed) {
                    return this.buildPalettePicker();
                }
                return [];
            }),
        };
    }
    buildPalettePicker() {
        return J2rGridColor.palette.map((row, rowIndex) => ({
            cl: 'col-row',
            key: rowIndex,
            children: row.map((colour, colIndex) => ({
                cl: 'cell',
                key: colIndex,
                style: { background: colour },
                onMouseDown: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                },
                onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    (this.props.onUpdate ?? _.noop)(this.stripHash(colour));
                    this.setState({ closed: true });
                },
            })),
        }));
    }
    stripHash(c) {
        if (!c) {
            return null;
        }
        else if (c.startsWith('#')) {
            return c.substring(1);
        }
        return c;
    }
    getColorValue() {
        let col = this.props.value ?? '';
        if (col && !col.startsWith('#')) {
            col = `#${col}`;
        }
        if (col && col.length !== 7) {
            console.warn(`Invalid colour: '${col}'`);
        }
        return col || null;
    }
}
export class J2rGridCheckbox extends React.Component {
    element;
    constructor(props) {
        super(props);
        Bindings(this, [this.toggle]);
        this.element = React.createRef();
        this.state = {
            checked: props.value ?? false,
            isCross: props.isCross ?? false,
        };
    }
    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            this.setState({ checked: Boolean(this.props.value) });
        }
    }
    render() {
        return j2r({
            ref: this.element,
            cl: BuildClass({
                'checkbox-renderer': true,
                checked: this.state.checked,
            }),
            text: Do(() => {
                if (!this.state.checked) {
                    return '';
                }
                else if (this.state.isCross) {
                    return '✘';
                }
                return '✔';
            }),
            onClick: this.toggle,
            onKeyPress: (e) => {
                if (e.keyCode === 13) {
                    this.toggle();
                }
            },
        });
    }
    toggle() {
        const new_value = !this.state.checked;
        this.setState({ checked: new_value }, () => (this.props.onUpdate ?? _.noop)(new_value));
    }
}
//# sourceMappingURL=component-react-grid.js.map